import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2 } from "lucide-react";
import { waLink } from "../../lib/constants";
import { track } from "../../lib/tracking";

const days = ["Today", "Tomorrow", "This weekend", "Any weekday"];
const times = ["Morning (10 AM – 1 PM)", "Afternoon (1 – 4 PM)", "Evening (4 – 8 PM)"];

const inputCls =
  "w-full rounded-xl border border-champagne/70 bg-ivory px-4 py-3.5 text-sm font-light text-ink placeholder:text-ink/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-teal focus:ring-2 focus:ring-teal/15";

export const LeadModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", phone: "", day: days[0], time: times[0], prior: "No" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setError("");
      document.body.style.overflow = "hidden";
      const onKey = (e) => e.key === "Escape" && onClose();
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
      };
    }
    document.body.style.overflow = "";
  }, [open, onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const phone = form.phone.replace(/\D/g, "").replace(/^91/, "");
    if (!form.name.trim() || phone.length < 10) {
      setError("Please enter your name and a valid 10-digit phone number.");
      return;
    }
    const msg = [
      "Hi Krystal Skin And Laser Clinic! I'd like to book the ₹22,500 Underarm Laser Hair Reduction Package.",
      `Name: ${form.name.trim()}`,
      `Phone: ${form.phone.trim()}`,
      `Preferred day: ${form.day}`,
      `Preferred time: ${form.time}`,
      `Had laser hair reduction before: ${form.prior}`,
    ].join("\n");
    track("lead_form_submit", { day: form.day, time: form.time, prior_laser: form.prior });
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
    setSubmitted(true);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lead-modal"
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-teal-900/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-t-[2rem] sm:rounded-[2rem] border border-champagne/60 bg-ivory p-7 md:p-9 shadow-2xl max-h-[92svh] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Book your package"
          >
            <button
              onClick={onClose}
              data-testid="lead-modal-close"
              aria-label="Close"
              className="absolute top-5 right-5 flex h-9 w-9 items-center justify-center rounded-full border border-champagne/70 text-ink/60 hover:text-teal hover:border-teal transition-colors duration-300"
            >
              <X className="w-4 h-4" strokeWidth={1.8} />
            </button>

            {submitted ? (
              <div className="py-10 text-center" data-testid="lead-success-message">
                <CheckCircle2 className="mx-auto w-12 h-12 text-teal" strokeWidth={1.2} />
                <h3 className="mt-6 font-display text-2xl md:text-3xl font-light text-ink">Thank you!</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-ink/60 max-w-xs mx-auto">
                  Our team will contact you shortly to confirm your consultation.
                </p>
                <button
                  onClick={onClose}
                  data-testid="lead-success-close"
                  className="mt-8 rounded-full bg-teal px-8 py-3.5 text-xs font-bold uppercase tracking-[0.16em] text-ivory hover:bg-teal-800 transition-colors duration-300"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-teal/60">Book the ₹22,500 Package</p>
                <h3 className="mt-3 font-display text-2xl md:text-3xl font-light tracking-tight text-ink">
                  Reserve your consultation
                </h3>
                <form onSubmit={submit} className="mt-7 space-y-4" data-testid="lead-form">
                  <input
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    data-testid="lead-name-input"
                    className={inputCls}
                    autoComplete="name"
                  />
                  <input
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Phone number"
                    inputMode="tel"
                    data-testid="lead-phone-input"
                    className={inputCls}
                    autoComplete="tel"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <select value={form.day} onChange={set("day")} data-testid="lead-day-select" className={inputCls} aria-label="Preferred day">
                      {days.map((d) => <option key={d}>{d}</option>)}
                    </select>
                    <select value={form.time} onChange={set("time")} data-testid="lead-time-select" className={inputCls} aria-label="Preferred time">
                      {times.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs font-light text-ink/55 mb-2.5">Have you had laser hair reduction before?</p>
                    <div className="flex gap-3">
                      {["Yes", "No"].map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, prior: v }))}
                          data-testid={`lead-prior-${v.toLowerCase()}`}
                          aria-pressed={form.prior === v}
                          className={`flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition-colors duration-300 ${
                            form.prior === v
                              ? "border-teal bg-teal text-ivory"
                              : "border-champagne/70 bg-ivory text-ink/60 hover:border-teal/50"
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                  {error && (
                    <p data-testid="lead-form-error" className="text-xs font-medium text-wine">{error}</p>
                  )}
                  <button
                    type="submit"
                    data-testid="lead-submit-button"
                    className="w-full rounded-full bg-teal px-8 py-4 text-sm font-bold uppercase tracking-[0.16em] text-ivory transition-colors duration-300 hover:bg-teal-800"
                  >
                    Confirm on WhatsApp
                  </button>
                  <p className="text-center text-[0.65rem] font-light text-ink/40 leading-relaxed">
                    Submitting opens WhatsApp with your details pre-filled. Consultation required; suitability assessed by the doctor.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
